/**
 * RevenuePage (REV-040 → REV-049).
 *
 * Finance-facing revenue console:
 *   - Generate a draft invoice from a case's captured charges.
 *   - Move invoices through review (approve) and submit, with per-row actions.
 *   - View the per-hospital AR aging report (GET /revenue/ar-aging).
 *
 * The Revenue service does not expose an invoice-list endpoint, so the table
 * shows invoices generated/mutated during this session (via {@link useRevenue}).
 * Data wiring is unchanged — this is presentation only.
 */
import { useEffect, useState, type CSSProperties } from 'react';
import { useArAging, useRevenue, type RevenueInvoice } from '../hooks/useRevenue';
import { colors } from '../theme';
import { PageHeader } from '../components/PageHeader';
import { ThreePanel } from '../components/ThreePanel';
import { Card } from '../components/Card';
import { DataTable, type Column } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { LoadingIndicator } from '../components/LoadingIndicator';

function money(amount: number | undefined): string {
  if (amount === undefined || amount === null || Number.isNaN(amount)) return '—';
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export function RevenuePage() {
  const { invoices, isWorking, error, generateInvoice, approveInvoice, submitInvoice } = useRevenue();
  const aging = useArAging();
  const [caseId, setCaseId] = useState('');

  useEffect(() => {
    aging.refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = caseId.trim();
    if (!trimmed) return;
    const created = await generateInvoice(trimmed);
    if (created) setCaseId('');
  };

  const invoiceColumns: Column<RevenueInvoice>[] = [
    {
      key: 'invoice',
      header: 'Invoice',
      render: (inv) => <span style={{ fontWeight: 600 }}>{inv.invoice_number ?? inv.invoice_id}</span>,
    },
    { key: 'case', header: 'Case', render: (inv) => <span style={muted}>{inv.case_id}</span> },
    { key: 'status', header: 'Status', render: (inv) => <StatusBadge value={inv.status} /> },
    { key: 'total', header: 'Total', align: 'right', render: (inv) => money(inv.total) },
    { key: 'balance', header: 'Balance', align: 'right', render: (inv) => money(inv.balance_due) },
    {
      key: 'actions',
      header: 'Actions',
      render: (inv) => {
        const canApprove = inv.status === 'draft';
        const canSubmit = inv.status === 'in_review' || inv.status === 'approved';
        return (
          <div style={actions}>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => approveInvoice(inv.invoice_id)}
              disabled={isWorking || !canApprove}
            >
              Approve
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => submitInvoice(inv.invoice_id)}
              disabled={isWorking || !canSubmit}
            >
              Submit
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader
        title="Revenue & Invoicing"
        subtitle="Generate, review, and submit invoices; monitor AR aging (REV-040 → REV-049)."
      />

      <ThreePanel
        primary={
          <>
            <Card title="Generate invoice">
              <form onSubmit={handleGenerate} style={generateForm}>
                <input
                  type="text"
                  value={caseId}
                  onChange={(e) => setCaseId(e.target.value)}
                  placeholder="Case ID (e.g. case-001)"
                  className="input"
                  aria-label="Case ID"
                  style={{ flex: 1 }}
                />
                <button type="submit" className="btn btn-primary" disabled={isWorking || !caseId.trim()}>
                  Generate
                </button>
              </form>
              {error && (
                <p style={errorText} role="alert">
                  {error.message}
                </p>
              )}
            </Card>

            <Card title="Invoices (this session)" padded={false}>
              {isWorking && (
                <div style={{ padding: 18 }}>
                  <LoadingIndicator text="Working…" />
                </div>
              )}
              <DataTable
                columns={invoiceColumns}
                rows={invoices}
                rowKey={(inv) => inv.invoice_id}
                emptyTitle="No invoices yet"
                emptyDescription="Generate one above to get started."
              />
            </Card>
          </>
        }
        rightContext={
          <div>
            <div style={summaryTitle}>AR aging</div>
            {aging.isLoading && <LoadingIndicator text="Loading…" />}
            {aging.error && (
              <p style={errorText} role="alert">
                {aging.error.message}
              </p>
            )}
            {aging.report && (
              <>
                <div style={agingHeadline}>{money(aging.report.total_outstanding)}</div>
                <div style={agingSub}>
                  outstanding across {aging.report.outstanding_invoice_count} invoice(s) · as of{' '}
                  {aging.report.as_of}
                </div>
                <dl style={bucketList}>
                  {aging.report.buckets.map((b) => (
                    <div key={b.label} style={bucketRow}>
                      <dt style={bucketLabel}>{b.label}</dt>
                      <dd style={bucketValue}>
                        {money(b.balance)} <span style={muted}>· {b.invoice_count}</span>
                      </dd>
                    </div>
                  ))}
                </dl>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={aging.refresh}
                  disabled={aging.isLoading}
                  style={{ marginTop: 12 }}
                >
                  Refresh
                </button>
              </>
            )}
          </div>
        }
      />
    </>
  );
}

const generateForm: CSSProperties = { display: 'flex', gap: 8, alignItems: 'center' };
const actions: CSSProperties = { display: 'flex', gap: 6 };
const muted: CSSProperties = { color: colors.textSecondary, fontSize: '0.86rem' };
const errorText: CSSProperties = { color: colors.danger, fontSize: '0.9rem', marginTop: 8 };
const summaryTitle: CSSProperties = {
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: colors.textSecondary,
  marginBottom: 8,
};
const agingHeadline: CSSProperties = { fontSize: '1.6rem', fontWeight: 700, color: colors.textPrimary };
const agingSub: CSSProperties = { fontSize: '0.8rem', color: colors.textSecondary, marginBottom: 12 };
const bucketList: CSSProperties = { margin: 0, display: 'flex', flexDirection: 'column' };
const bucketRow: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  padding: '7px 0',
  borderBottom: `1px solid ${colors.surfaceAlt}`,
};
const bucketLabel: CSSProperties = { fontSize: '0.84rem', color: colors.textSecondary };
const bucketValue: CSSProperties = { margin: 0, fontSize: '0.86rem', fontWeight: 600 };
